import { useState, useEffect, useRef } from 'react';
import type SharedAiSourceSelection from '@/types/serializers/SharedAiSourceSelection';
import useCreateAiJobSearchPersona from '../api/useCreateAiJobSearchPersona';
import useFetchAiJobSearchPersona from '../api/useFetchAiJobSearchPersona';
import useFetchLatestAiJobSearchPersona from '../api/useFetchLatestAiJobSearchPersona';
import useRebuildAiJobSearchPersona from '../api/useRebuildAiJobSearchPersona';
import useCreateAiJobSearchRun from '../api/useCreateAiJobSearchRun';
import useFetchAiJobSearchRun from '../api/useFetchAiJobSearchRun';
import useFetchLatestAiJobSearchRun from '../api/useFetchLatestAiJobSearchRun';
import useFetchAiJobSearchRunResults from '../api/useFetchAiJobSearchRunResults';

/**
 * フローのステージ定義
 *
 * - idle: 初期状態（ペルソナ未作成）
 * - persona_building: ペルソナ構築中（queued/processing ポーリング中）
 * - persona_ready: ペルソナ完成（表示・編集可能）
 * - persona_failed: ペルソナ構築失敗
 * - search_starting: 検索ラン起動中
 * - search_polling: 検索ラン queued/processing ポーリング中
 * - search_completed: 検索完了（結果表示）
 * - search_failed: 検索失敗
 */
export type FlowStage =
  | 'idle'
  | 'persona_building'
  | 'persona_ready'
  | 'persona_failed'
  | 'search_starting'
  | 'search_polling'
  | 'search_completed'
  | 'search_failed';

type StartOptions = {
  candidateProfileId: number;
  selectedSources?: string[];
  /** カテゴリ内の個別ID選択（決定1） */
  sourceSelection?: Partial<SharedAiSourceSelection>;
  freeText?: string;
};

/**
 * AI求人検索のフローを管理するフック
 * ペルソナ構築 → 検索ラン → 結果 の3ステージを一元管理する
 *
 * ステート保持の設計方針:
 * ダイアログを閉じても stage / personaId / runId は保持する。
 * 同一候補者でダイアログを再オープンした場合は前回のペルソナ状態から再開できる。
 * 候補者を切り替えた場合は、利用側コンポーネントに渡される候補者ID由来の `key`
 * によってコンポーネントがアンマウントされ、このフックの状態も自動でリセットされる。
 */
export default function useAiJobSearchFlow(candidateProfileId: number | null, enabled = true) {
  const [stage, setStage] = useState<FlowStage>('idle');
  const [personaId, setPersonaId] = useState<number | null>(null);
  const [runId, setRunId] = useState<number | null>(null);

  // 復元（決定3）の二重実行防止。ダイアログを開いている間に一度だけ最新ペルソナ/Runを引く。
  const hasRestoredPersonaRef = useRef(false);
  // 復元シーケンス: ペルソナ復元で persona_ready になった直後のみ最新Runを引く
  const [isRestoringRun, setIsRestoringRun] = useState(false);
  // 自動検索の多重起動防止。persona_ready で startSearch を1回だけ走らせるためのガード。
  const isAutoSearchingRef = useRef(false);

  const { mutateAsync: createPersona, isPending: isCreatingPersona } =
    useCreateAiJobSearchPersona();
  const { mutateAsync: rebuildPersona, isPending: isRebuildingPersona } =
    useRebuildAiJobSearchPersona();
  const { mutateAsync: createRun, isPending: isCreatingRun } = useCreateAiJobSearchRun();

  // ペルソナ取得: personaId があれば常に取得する（source_selection / display_text を
  // 結果表示中も参照できるようにするため）。実際のポーリングは useFetchAiJobSearchPersona
  // 側で status が queued/processing のときのみ行われ、completed では1回取得して停止する。
  const {
    data: personaData,
    error: personaError,
    isError: isPersonaError,
  } = useFetchAiJobSearchPersona(personaId);

  // 検索ランポーリング
  const {
    data: runData,
    error: runError,
    isError: isRunError,
  } = useFetchAiJobSearchRun(
    stage === 'search_polling' || stage === 'search_starting' ? runId : null
  );

  // 検索結果取得: 完了状態になったときのみ
  const {
    data: resultsData,
    isLoading: isResultsLoading,
    isError: isResultsError,
    error: resultsError,
  } = useFetchAiJobSearchRunResults(stage === 'search_completed' ? runId : null);

  // 最新ペルソナ復元（決定3）: ダイアログ表示中かつ idle・未復元のときのみ取得する。
  // 該当なしなら null が返り idle のまま。
  const shouldRestorePersona = enabled && stage === 'idle' && !hasRestoredPersonaRef.current;
  const { data: latestPersona } = useFetchLatestAiJobSearchPersona(
    shouldRestorePersona ? candidateProfileId : null
  );

  // 最新Run復元（決定3）: ペルソナ復元で persona_ready になった直後のみ最新Runを引く。
  // 新規構築・rebuild 経由では isRestoringRun を立てないため、古いRunを誤って復元しない。
  const { data: latestRun } = useFetchLatestAiJobSearchRun(isRestoringRun ? personaId : null);

  // 復元中フラグ: ダイアログを開いてから最新ペルソナ/Run の復元が確定するまで true。
  // この間は idle 画面（自動で探す）ではなくローディングを表示し、
  // idle → 復元 → 結果 の中間状態がちらついて見えるのを防ぐ。
  const isRestoring = (shouldRestorePersona && latestPersona === undefined) || isRestoringRun;

  // 最新ペルソナの取得結果を反映する
  useEffect(() => {
    if (!shouldRestorePersona) return;
    // クエリ未解決（undefined）の間は待つ。null は「該当ペルソナなし」を表す。
    if (latestPersona === undefined) return;

    hasRestoredPersonaRef.current = true;
    if (!latestPersona) return;

    setPersonaId(latestPersona.id);
    if (latestPersona.status === 'completed') {
      setStage('persona_ready');
      setIsRestoringRun(true);
    } else if (latestPersona.status === 'failed') {
      setStage('persona_failed');
    } else {
      // queued/processing → ポーリングを再開する
      setStage('persona_building');
    }
  }, [shouldRestorePersona, latestPersona]);

  // 最新Runの取得結果を反映する
  useEffect(() => {
    if (!isRestoringRun) return;
    if (latestRun === undefined) return;

    setIsRestoringRun(false);
    if (!latestRun) return; // Runなし → persona_ready のまま

    setRunId(latestRun.id);
    if (latestRun.status === 'completed') {
      setStage('search_completed');
    } else if (latestRun.status === 'failed') {
      setStage('search_failed');
    } else {
      // queued/processing → ポーリングを再開する
      setStage('search_polling');
    }
  }, [isRestoringRun, latestRun]);

  // ペルソナのポーリング結果を監視してステージを更新
  useEffect(() => {
    if (stage !== 'persona_building') return;
    if (!personaData) return;

    if (personaData.status === 'completed') {
      setStage('persona_ready');
    } else if (personaData.status === 'failed') {
      setStage('persona_failed');
    }
    // queued/processing はそのまま polling が継続される
  }, [personaData, stage]);

  // ペルソナ取得エラー
  useEffect(() => {
    if (isPersonaError && stage === 'persona_building') {
      setStage('persona_failed');
    }
  }, [isPersonaError, stage]);

  // 検索ランのポーリング結果を監視してステージを更新
  useEffect(() => {
    if (stage !== 'search_polling') return;
    if (!runData) return;

    if (runData.status === 'completed') {
      setStage('search_completed');
    } else if (runData.status === 'failed') {
      setStage('search_failed');
    }
  }, [runData, stage]);

  // 検索ラン取得エラーまたは結果取得エラー
  useEffect(() => {
    if (
      (isRunError || isResultsError) &&
      (stage === 'search_polling' || stage === 'search_completed')
    ) {
      setStage('search_failed');
    }
  }, [isRunError, isResultsError, stage]);

  /**
   * ペルソナ構築を開始する（候補者情報から入力タイプ: candidate）
   */
  const startPersonaBuild = async ({
    candidateProfileId: targetCandidateProfileId,
    selectedSources,
    sourceSelection,
    freeText,
  }: StartOptions) => {
    try {
      setStage('persona_building');
      const persona = await createPersona({
        input_type: 'candidate',
        candidate_profile_id: targetCandidateProfileId,
        selected_sources: selectedSources,
        source_selection: sourceSelection,
        free_text: freeText,
      });
      setPersonaId(persona.id);
      // 作成直後のステータスに応じてステージを設定
      if (persona.status === 'completed') {
        setStage('persona_ready');
      } else if (persona.status === 'failed') {
        setStage('persona_failed');
      }
      // queued/processing はそのまま persona_building を維持してポーリングを開始
    } catch {
      setStage('persona_failed');
    }
  };

  /**
   * ペルソナを再構築する（既存ペルソナIDに対して rebuild）
   */
  const rebuildCurrentPersona = async (options?: {
    selectedSources?: string[];
    sourceSelection?: Partial<SharedAiSourceSelection>;
    freeText?: string;
  }) => {
    if (!personaId) return;
    try {
      setStage('persona_building');
      const persona = await rebuildPersona({
        id: personaId,
        selected_sources: options?.selectedSources,
        source_selection: options?.sourceSelection,
        free_text: options?.freeText,
      });
      // rebuild API は queued な persona を返す
      if (persona.status === 'completed') {
        setStage('persona_ready');
      } else if (persona.status === 'failed') {
        setStage('persona_failed');
      }
      // queued/processing はそのまま persona_building でポーリングを継続
    } catch {
      setStage('persona_failed');
    }
  };

  /**
   * 検索ランを開始する（persona_ready ステージから呼ぶ）
   */
  const startSearch = async () => {
    if (!personaId) return;
    try {
      setStage('search_starting');
      const run = await createRun({ job_search_persona_id: personaId });
      setRunId(run.id);
      if (run.status === 'completed') {
        setStage('search_completed');
      } else if (run.status === 'failed') {
        setStage('search_failed');
      } else {
        setStage('search_polling');
      }
      // setRunId 後に useFetchAiJobSearchRun の enabled が true になり自動フェッチが走るため
      // invalidateQueries は不要（新規作成直後でキャッシュは存在しない）
    } catch {
      setStage('search_failed');
    }
  };

  // ペルソナ確認ステップは廃止。ペルソナ構築が完了 (persona_ready) したら
  // 確認画面を挟まず即座に求人検索を開始する。
  // - 新規構築 / rebuild / ポーリング完了: いずれも persona_ready 経由で自動発火
  // - 復元 (isRestoringRun 中): 既存Runの有無を確認している間は待機し、
  //   Runが無い場合 (isRestoringRun が true→false でも persona_ready のまま) のみ自動検索する
  // - retrySearch (検索失敗→persona_ready に戻す) もこの経路で自動再検索される
  // startSearch 内で stage を search_starting へ進めるため通常はループしないが、
  // StrictMode / 再レンダリングでの createRun 二重呼び出しを防ぐため ref ガードを併用する。
  // startSearch はレンダーごとに再生成されるが、依存する personaId は deps に含むため除外する
  // （本プロジェクトでは react-hooks/exhaustive-deps ルールは未設定）。
  useEffect(() => {
    if (stage !== 'persona_ready') return;
    if (isRestoringRun) return;
    if (!personaId) return;
    if (isAutoSearchingRef.current) return;
    isAutoSearchingRef.current = true;
    void startSearch().finally(() => {
      isAutoSearchingRef.current = false;
    });
  }, [stage, isRestoringRun, personaId]);

  /**
   * ペルソナ構築失敗からのリトライ（idle に戻す）
   */
  const retryPersonaBuild = () => {
    setStage('idle');
    setPersonaId(null);
    setRunId(null);
  };

  /**
   * 検索失敗からのリトライ（persona_ready に戻す）
   */
  const retrySearch = () => {
    setStage('persona_ready');
    setRunId(null);
  };

  const isPersonaProcessing =
    stage === 'persona_building' || isCreatingPersona || isRebuildingPersona;
  const isSearchProcessing =
    stage === 'search_starting' || stage === 'search_polling' || isCreatingRun;
  const isProcessing = isPersonaProcessing || isSearchProcessing;

  // ペルソナのプログレスメッセージ
  const personaProgressMessage =
    stage === 'persona_building'
      ? (personaData?.progress_message ?? 'プロフィールを分析しています...')
      : null;

  // 検索のプログレスメッセージ
  const searchProgressMessage =
    stage === 'search_polling' || stage === 'search_starting'
      ? (runData?.progress_message ?? '求人を検索しています...')
      : null;

  const personaErrorMessage =
    stage === 'persona_failed'
      ? (personaData?.error_message ??
        personaError?.message ??
        'ペルソナ構築中にエラーが発生しました')
      : null;

  const searchErrorMessage =
    stage === 'search_failed'
      ? (runData?.error_message ??
        runError?.message ??
        resultsError?.message ??
        '検索中にエラーが発生しました')
      : null;

  return {
    stage,
    personaId,
    runId,
    // ポーリングで取得した最新ペルソナデータ
    personaData,
    runData,
    resultsData,
    isPersonaProcessing,
    isSearchProcessing,
    isProcessing,
    isRestoring,
    isResultsLoading,
    personaProgressMessage,
    searchProgressMessage,
    personaErrorMessage,
    searchErrorMessage,
    startPersonaBuild,
    rebuildCurrentPersona,
    startSearch,
    retryPersonaBuild,
    retrySearch,
  };
}
